/**
 * Research Job Orchestrator contract — E06-F02 (BullMQ per-run state machine +
 * SSE progress + polling fallback). The single source of truth for the run-level
 * status/stage enums, the SSE `progress`/`done` event payloads, the polling
 * `research_status` payload, and the start/cancel/retry command DTOs.
 *
 * Parity source (1:1 port): docs/architecture.md §7 + the old EER
 * `company-research-architecture.md` (§3, §4, §6.3-6.5, §6.13). Stage-2 was never
 * deployed to legacy n8n prod, so the canonical contract is the architecture doc
 * + migration 023 DDL (research_jobs) — there is no live n8n node body to copy.
 *
 * INVARIANTS encoded here (authoritative, from the task acceptance criteria):
 *   - job.status ∈ queued|running|paused|completed|failed|cancelled.
 *     `paused` = awaiting_review. Since E06-F05-T04 the automatic run reaches it
 *     after `dedup` (candidates ready) in BOTH review modes; the legacy
 *     "pause after scoring" gate is dormant (nothing walks to `scoring` on its own).
 *   - current_stage ∈ intake|normalize|discovery|dedup|disambiguation|enrichment|
 *     verify|scoring|review|publish (run-level stages, distinct from the
 *     per-company research_companies.stage enum).
 *   - state machine: queued → running → (paused at gate) → completed; branches:
 *     failed (retry → running) and cancelled (terminal). review_mode='auto' goes
 *     running → completed without a pause.
 *   - partial results are first-class: top_partial carries the already-scored rows
 *     at every progress tick and survives cancel/crash.
 *   - SSE: token in the QUERY (?session_token=), text/event-stream; `progress`
 *     every ~1-2s or on change; `done` closes the stream (job_status='paused' at
 *     gate so the SPA opens the review table).
 *   - crash-resume: heartbeat_at written while running; a stale running job
 *     (heartbeat_at < now-interval) is safely taken over — only companies with
 *     stage NOT IN ('scored','failed') are re-processed (idempotent upsert → no
 *     double-work).
 *
 * This file is feature-owned. It is re-exported from `@eer/shared` by the serial
 * Wire pass — it MUST NOT edit the shared barrel during parallel work.
 */
import { z } from 'zod';
// The Run screen renders the request's OWN criteria next to the run progress
// (E06-F02-T10), so the status snapshot carries the intake shapes verbatim. Both
// symbols live in the intake contract; that file imports only from `../jsonb.js`,
// so this direction adds no cycle.
import { intakeCriteriaSchema, researchRequestStatusSchema, researchReviewModeSchema, } from './research-intake.js';
import { fanoutTileSchema } from './research-pipeline.js';
import { discoveryExclusionSchema } from './research-discovery-exclusions.js';
// ---------------------------------------------------------------------------
// Discovery issues — a provider search that failed for one fan-out tile
// (E07-F02-T11). Persisted on research_jobs.discovery_issues.
// ---------------------------------------------------------------------------
/**
 * Machine-readable cause of a failed discovery search. Branch on the code, never
 * on the wording of `message`.
 *   - location_unresolved  — the location label cannot be expressed as provider
 *                            filters (no call was made);
 *   - provider_rejected    — the provider answered 400/422 (bad request);
 *   - provider_auth        — the provider refused our credentials (401/403, token);
 *   - rate_limited         — the provider answered 429;
 *   - provider_unavailable — the provider is not configured / 5xx;
 *   - provider_unreachable — network failure, no HTTP answer;
 *   - unknown              — anything else.
 * Two NOTICE codes (E07-F02-T14) — the search ran fine but found 0 companies;
 * recorded so a zero is never silent, never counted as a failed search:
 *   - subcategory_unmapped — the sub-category matched no industry-map rule, so it
 *                            was searched as a raw phrase, and that found nothing;
 *   - no_results           — the dictionary filters were applied, the provider
 *                            found nothing for them in this location / size.
 */
export const DISCOVERY_ISSUE_CODES = [
    'location_unresolved',
    'provider_rejected',
    'provider_auth',
    'rate_limited',
    'provider_unavailable',
    'provider_unreachable',
    'unknown',
    'subcategory_unmapped',
    'no_results',
];
export const discoveryIssueCodeSchema = z.enum(DISCOVERY_ISSUE_CODES);
/** Issue codes that explain an EMPTY search rather than a failed one (E07-F02-T14). */
export const DISCOVERY_NOTICE_CODES = [
    'subcategory_unmapped',
    'no_results',
];
/** True when the issue is a "found nothing, here is why" notice, not a failure. */
export function isDiscoveryNotice(issue) {
    return DISCOVERY_NOTICE_CODES.includes(issue.code);
}
/**
 * One failed discovery search: which tile, why (human sentence), and the raw
 * technical detail (HTTP status + provider body) for a "details" disclosure. No
 * automatic retry ever follows an issue — the human decides (edit criteria / retry).
 */
export const discoveryIssueSchema = z.object({
    tile: fanoutTileSchema,
    code: discoveryIssueCodeSchema.catch('unknown'),
    message: z.string(),
    detail: z.string().nullable().default(null),
});
/** Human label of a fan-out tile: "Pumps · Europe · 50-200" (null axes skipped). */
export function discoveryTileLabel(tile) {
    const parts = [tile.subIndustry, tile.geoTile, tile.sizeBand]
        .map((p) => (p ?? '').trim())
        .filter((p) => p !== '');
    return parts.length > 0 ? parts.join(' · ') : 'All companies';
}
// ---------------------------------------------------------------------------
// Discovery tile results — one row per fan-out tile (E07-F02-T17). Persisted on
// research_jobs.discovery_tiles. Answers "what did each search find, and if it
// added nothing — why": 8 of 34 tiles gave 0 new rows with no visible reason.
// ---------------------------------------------------------------------------
/**
 * The filters one discovery search actually sent (as the provider reported them).
 * Every field is defaulted: the AI discovery reports no filters (`null` on the
 * stat), a structured provider fills what it used.
 */
export const discoveryTileFiltersSchema = z.object({
    /** How the sub-category was matched: dictionary rule / raw phrase fallback / request industry / none. */
    industrySource: z
        .enum(['rule', 'fallback', 'industry', 'none'])
        .nullable()
        .catch(null)
        .default(null),
    /** The industry-map rule that matched (source='rule'). */
    matchedRule: z.string().nullable().default(null),
    industryCodes: z.array(z.string()).default([]),
    keywords: z.array(z.string()).default([]),
    /** Resolved location(s) the search was pinned to (countries / states / metros / cities). */
    geo: z.array(z.string()).default([]),
    employees: z
        .object({ min: z.number().nullable().default(null), max: z.number().nullable().default(null) })
        .nullable()
        .default(null),
    foundedFrom: z.number().nullable().default(null),
    foundedTo: z.number().nullable().default(null),
    /** E07-F02-T16 — the relaxed key a second search used after the strict one ran out. */
    relaxedKey: z.string().nullable().default(null),
    /** How many provider searches the tile made (one per resolved place). */
    searches: z.number().int().default(0),
});
/**
 * One fan-out tile's outcome:
 *   - found      — companies the search returned (before any dedup);
 *   - duplicates — of those, already found by an earlier tile of this run or
 *                  already on the request's list (never inserted twice);
 *   - excluded   — cut by the request's Do-Not-Source lists (E07-F02-T15);
 *   - created    — NEW long-list rows this tile added;
 *   - capped     — the request ceiling was already reached: the tile was not searched;
 *   - issue      — the tile's failure / zero-result notice (same as discoveryIssues).
 */
export const discoveryTileStatSchema = z.object({
    tile: fanoutTileSchema,
    found: z.number().int().default(0),
    duplicates: z.number().int().default(0),
    excluded: z.number().int().default(0),
    created: z.number().int().default(0),
    /** The provider's own result total for the query (null = not reported). */
    providerTotal: z.number().nullable().default(null),
    capped: z.boolean().default(false),
    filters: discoveryTileFiltersSchema.nullable().default(null),
    issue: z
        .object({ code: discoveryIssueCodeSchema.catch('unknown'), message: z.string() })
        .nullable()
        .default(null),
    /** E06-F01-T10 — the location stage of the tile's geo (absent = one-stage request). */
    stage: z.number().int().positive().optional(),
    /**
     * E06-F01-T10 — not searched: the earlier location stages already reached the
     * request target, so this later stage was not needed.
     */
    stageSkipped: z.boolean().optional(),
});
/**
 * Why a tile added no new rows — a human sentence, or null when it did add rows.
 * Pure; shared so the API tests and the SPA table say the same thing.
 */
export function discoveryTileZeroReason(stat) {
    if (stat.created > 0)
        return null;
    if (stat.stageSkipped) {
        return `Not searched: the earlier location stage${stat.stage && stat.stage > 2 ? 's' : ''} already found enough companies for the request target.`;
    }
    if (stat.issue)
        return stat.issue.message;
    if (stat.capped) {
        return 'Not searched: the request already reached its company ceiling before this search.';
    }
    if (stat.found === 0)
        return 'The search returned no companies for these filters.';
    const parts = [];
    if (stat.duplicates > 0) {
        parts.push(`${stat.duplicates} of ${stat.found} were already found by an earlier search or are already on the list`);
    }
    if (stat.excluded > 0)
        parts.push(`${stat.excluded} are on the Do-Not-Source lists`);
    const rest = stat.found - stat.duplicates - stat.excluded;
    if (rest > 0)
        parts.push(`${rest} had no usable name or website`);
    return `No new companies: ${parts.join('; ')}.`;
}
/** Human one-liner of a tile's filters ("industries 123 + keyword "pumps" · Germany · 50–200 employees"). */
export function describeDiscoveryTileFilters(f) {
    if (!f)
        return '—';
    const parts = [];
    if (f.industryCodes.length > 0)
        parts.push(`industries ${f.industryCodes.join(', ')}`);
    if (f.keywords.length > 0)
        parts.push(`keyword "${f.keywords.join('", "')}"`);
    if (f.matchedRule)
        parts.push(`rule "${f.matchedRule}"`);
    else if (f.industrySource === 'fallback')
        parts.push('no dictionary rule (raw phrase)');
    if (f.geo.length > 0) {
        const shown = f.geo.slice(0, 6).join(', ');
        parts.push(f.geo.length > 6 ? `${shown} +${f.geo.length - 6}` : shown);
    }
    if (f.employees && (f.employees.min != null || f.employees.max != null)) {
        const { min, max } = f.employees;
        parts.push(min != null && max != null
            ? `${min}–${max} employees`
            : min != null
                ? `${min}+ employees`
                : `up to ${max} employees`);
    }
    if (f.foundedFrom != null || f.foundedTo != null) {
        parts.push(`founded ${f.foundedFrom ?? '…'}–${f.foundedTo ?? '…'}`);
    }
    if (f.relaxedKey)
        parts.push(`relaxed key "${f.relaxedKey}"`);
    if (f.searches > 1)
        parts.push(`${f.searches} searches`);
    return parts.length > 0 ? parts.join(' · ') : 'no filters';
}
// ---------------------------------------------------------------------------
// Run-level job status (research_jobs.status)
// ---------------------------------------------------------------------------
/** research_jobs.status values (schema.prisma:459, migration 023). */
export const JOB_STATUSES = [
    'queued',
    'running',
    'paused', // = awaiting_review (review_mode='gate', after scoring)
    'completed',
    'failed',
    'cancelled',
];
export const jobStatusSchema = z.enum(JOB_STATUSES);
/** Terminal statuses — a job in one of these is done (no further transitions). */
export const TERMINAL_JOB_STATUSES = ['completed', 'failed', 'cancelled'];
export function isTerminalJobStatus(status) {
    return TERMINAL_JOB_STATUSES.includes(status);
}
/** Statuses that count as an ACTIVE run (idempotency: research_start → CONFLICT). */
export const ACTIVE_JOB_STATUSES = ['queued', 'running'];
export function isActiveJobStatus(status) {
    return ACTIVE_JOB_STATUSES.includes(status);
}
// ---------------------------------------------------------------------------
// Run-level orchestrator stage (research_jobs.current_stage) — distinct from the
// per-company research_companies.stage enum.
// ---------------------------------------------------------------------------
/** research_jobs.current_stage values (run-level, architecture §4). */
export const ORCHESTRATOR_STAGES = [
    'intake',
    'normalize',
    'discovery',
    'dedup',
    'disambiguation',
    'enrichment',
    'verify',
    'scoring',
    'review',
    'publish',
];
export const orchestratorStageSchema = z.enum(ORCHESTRATOR_STAGES);
/**
 * The ordered run-level pipeline. The orchestrator advances current_stage along
 * this list. `review` is the gate point (pause at review_mode='gate'); `publish`
 * completes the run.
 */
export const ORCHESTRATOR_STAGE_ORDER = [
    'normalize',
    'discovery',
    'dedup',
    'disambiguation',
    'enrichment',
    'verify',
    'scoring',
    'review',
    'publish',
];
/**
 * The AUTOMATIC (free) part of the run pipeline — E06-F05-T04.
 *
 * A run started by "Прогнать" walks ONLY these stages: normalize → discovery →
 * dedup. All three are free (criteria normalisation, the discovery seed, the
 * companies-library dedup); none of them calls a paid provider. After `dedup` the
 * run PARKS at "candidates ready" (job='paused', request='in_review') and waits
 * for a human — the paid AI work is the separate, per-company, click-initiated AI
 * chain (`aiRunStatus`), never a continuation of this walk.
 *
 * `ORCHESTRATOR_STAGE_ORDER` above stays the FULL legacy order: it is the
 * current_stage vocabulary (DB values stay valid) and the dormant legacy path.
 *
 * PROVENANCE (E12-F06-T01): this guard and the two below (`AUTO_RUN_TERMINAL_STAGE`,
 * `LEGACY_AI_FANOUT_STAGES`) were delivered in commit df9095f, whose message reads
 * `feat(E06-F05-T05)` — so `git log --grep=E06-F05-T04` does NOT find them, and
 * `git revert df9095f` "to undo T05" would remove this money guard. Before touching
 * that commit read docs/commit-provenance.md.
 */
export const AUTO_ORCHESTRATOR_STAGE_ORDER = [
    'normalize',
    'discovery',
    'dedup',
];
/** The last stage the automatic run performs; after it the run parks (T04). */
export const AUTO_RUN_TERMINAL_STAGE = 'dedup';
/** The run-level stages that fan the long list out to the company queue (free part). */
export const AUTO_FANOUT_STAGES = [
    'discovery',
    'dedup',
];
/**
 * DORMANT (E06-F05-T04): the run-level stages the OLD automatic path fanned out
 * after `dedup` — every one of them drove PAID per-company work (disambiguate /
 * enrich / verify / score). The orchestrator no longer walks them; the code is
 * retained (see `researchOrchestrator.isLegacyFanoutStage`) but unreachable
 * automatically. This is the financial-risk surface the task removed.
 */
export const LEGACY_AI_FANOUT_STAGES = [
    'disambiguation',
    'enrichment',
    'verify',
    'scoring',
];
/**
 * DORMANT (E06-F05-T04): the stage AFTER which a review_mode='gate' run used to
 * pause (awaiting_review). The automatic run now parks after
 * `AUTO_RUN_TERMINAL_STAGE` in BOTH review modes — `scoring` is never reached
 * automatically — so this constant only describes the retained legacy path.
 */
export const GATE_PAUSE_AFTER_STAGE = 'scoring';
// ---------------------------------------------------------------------------
// Review mode (research_requests.review_mode)
// ---------------------------------------------------------------------------
export const REVIEW_MODES = ['gate', 'auto'];
export const reviewModeSchema = z.enum(REVIEW_MODES);
// ---------------------------------------------------------------------------
// Heartbeat / stale detection (crash-resume, T07)
// ---------------------------------------------------------------------------
/**
 * A running job whose heartbeat is older than this interval is STALE and may be
 * safely taken over by another worker. The worker writes heartbeat_at on every
 * tick; 60s gives ample slack over the ~1-2s progress cadence.
 */
export const HEARTBEAT_STALE_MS = 60_000;
/** How often the running worker writes heartbeat_at. */
export const HEARTBEAT_WRITE_MS = 5_000;
/**
 * The per-company stages whose rows are ALREADY done — a takeover never
 * re-processes these (idempotency: only stage NOT IN ('scored','failed') is
 * re-run). Mirrors research_companies.stage terminal values.
 */
export const COMPLETED_COMPANY_STAGES = ['scored', 'failed'];
export function isCompletedCompanyStage(stage) {
    return COMPLETED_COMPANY_STAGES.includes(stage);
}
// ---------------------------------------------------------------------------
// SSE / polling progress payloads
// ---------------------------------------------------------------------------
/** A top-N already-scored row carried in the partial result (first-class). */
export const partialRowSchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    grade: z.enum(['A', 'B', 'C']).nullable(),
    score: z.number().nullable(),
    needsHuman: z.boolean(),
});
/** Default size of top_partial[] carried in each progress tick. */
export const TOP_PARTIAL_LIMIT = 20;
/**
 * SSE `progress` event payload. Emitted every ~1-2s or on change while the run is
 * active. `top_partial` carries the already-scored rows (partial = first-class).
 */
export const progressEventSchema = z.object({
    requestId: z.number(),
    jobStatus: jobStatusSchema,
    currentStage: orchestratorStageSchema.nullable(),
    completed: z.number(),
    total: z.number(),
    errorCount: z.number(),
    topPartial: z.array(partialRowSchema),
});
/**
 * SSE `done` event payload — closes the stream on finish/pause. At review_mode=
 * 'gate' the run pauses and `jobStatus='paused'` so the SPA opens the review
 * table; at 'auto' it carries 'completed'.
 */
export const doneEventSchema = z.object({
    requestId: z.number(),
    jobStatus: jobStatusSchema,
});
/** SSE event names this stream emits. */
export const SSE_EVENT_NAMES = { progress: 'progress', done: 'done' };
/**
 * The REQUEST context carried alongside the run snapshot — E06-F02-T10.
 *
 * BUG: the Run screen used to know nothing about the request it was showing. Its
 * title came from a host-carried prop that open-by-status never filled, and the
 * criteria were not part of any payload the screen fetched, so re-entering Run for
 * an already-finished request (`in_review`: opened on Review, then "back") rendered
 * a title-less shell with an empty partial table — the recruiter lost the criteria
 * the search was run with. The snapshot now carries the request itself, so the
 * screen shows the same criteria it showed on the first run, on every re-entry and
 * in every run state (progress / failed / low-yield).
 *
 * `criteria` is the machine-valued {@link intakeCriteriaSchema} blob stored in
 * `research_requests.criteria` (every field defaulted, so a partially-filled or
 * legacy row still parses).
 */
export const runRequestSummarySchema = z.object({
    id: z.number(),
    title: z.string().default(''),
    status: researchRequestStatusSchema,
    criteria: intakeCriteriaSchema,
    reviewMode: researchReviewModeSchema.nullable().default(null),
    targetCount: z.number().nullable().default(null),
    /** E06-F01-T14 — the linked project (Run shows its recruiter + team leads). */
    projectId: z.number().nullable().default(null),
});
/**
 * Polling fallback payload (GET /research/:id/status). Same data the SSE
 * `progress` carries, shaped as a snapshot the SPA polls every 3-5s when
 * EventSource is unavailable (CORS / no EventSource).
 *
 * `request` (E06-F02-T10) is the request context the Run screen renders (title +
 * criteria). It is nullish rather than required so an older/partial payload — and
 * any test double that only fakes the job aggregate — still parses.
 */
export const researchStatusResponseSchema = z.object({
    job: z.object({
        status: jobStatusSchema,
        currentStage: orchestratorStageSchema.nullable(),
        total: z.number(),
        completed: z.number(),
        errorCount: z.number(),
        lastError: z.string().nullable(),
        /**
         * E07-F02-T11 — discovery searches that failed (one per tile). Non-empty on a
         * `paused` job = PARTIAL discovery (the Review screen warns); on a `failed` job
         * = every search failed (`lastError` carries the summary). Optional so an older
         * API build's payload still parses mid-deploy.
         */
        discoveryIssues: z.array(discoveryIssueSchema).optional(),
        /**
         * E07-F02-T17 — one row per discovery tile: found / duplicates / new + the
         * filters sent + the failure or zero reason. Empty for runs made before T17.
         * Optional so an older API build's payload still parses mid-deploy.
         */
        discoveryTiles: z.array(discoveryTileStatSchema).optional(),
        /**
         * E07-F02-T18 — companies cut at discovery (DNS company / DNS industry / size)
         * with the reason; its length is the "excluded at search" counter. Optional so
         * an older API build's payload still parses mid-deploy.
         */
        discoveryExcluded: z.array(discoveryExclusionSchema).optional(),
    }),
    topPartial: z.array(partialRowSchema),
    request: runRequestSummarySchema.nullish(),
});
// ---------------------------------------------------------------------------
// Command DTOs (start / cancel / retry)
// ---------------------------------------------------------------------------
/** research_start response — the enqueued run row. */
export const researchStartResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    jobId: z.number(),
    requestId: z.number(),
    status: jobStatusSchema,
});
/** research_cancel response. */
export const researchCancelResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    jobId: z.number(),
    status: jobStatusSchema, // 'cancelled'
});
/**
 * research_retry input. `company_id` present → retry one failed long-list row;
 * null/absent → re-run the whole job from where it stopped (job → running).
 */
export const researchRetryInputSchema = z.object({
    companyId: z.number().int().positive().nullable().optional(),
});
export const researchRetryResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    jobId: z.number(),
    status: jobStatusSchema, // 'running'
    /** When a single row was retried, its id; else null (whole-job retry). */
    retriedCompanyId: z.number().nullable(),
});
//# sourceMappingURL=research-orchestrator.js.map